import { useState } from 'react';
import { useLazyQuery, gql } from '@apollo/client';
import { AreaType } from 'graphql/queries/getAreasByWarehouseId';
import { WAREHOUSE_FIELDS, WarehouseType } from 'graphql/types/WarehouseType';

// 获取所有warehouse的完整信息（包括deliveryZoneZipcodeList）用于冲突检查
const GET_ALL_WAREHOUSES_FOR_CONFLICT_CHECK = gql`
  query GetAllWarehousesForConflictCheck {
    warehouses: getWarehouseListForHeadquarters {
      ...WarehouseFields
    }
  }
  ${WAREHOUSE_FIELDS}
`;

interface AllWarehousesResponse {
  warehouses: WarehouseType[];
}

export interface ZipcodeConflictResult {
  hasAreaConflict: boolean;
  hasWarehouseConflict: boolean;
  conflictingAreas: Array<AreaType & { conflictingZipcodes: string[] }>;
  conflictingWarehouses: Array<{ warehouseId: number; warehouseName: string; zipcodes: string[] }>;
}

export interface ConflictCheckOptions {
  originalZipcodes: string[];
  newZipcodes: string[];
  currentWarehouseId: number;
  areas: AreaType[];
}

export { GET_ALL_WAREHOUSES_FOR_CONFLICT_CHECK };
export const useZipcodeConflictCheck = () => {
  const [loading, setLoading] = useState(false);
  
  const [getAllWarehouses] = useLazyQuery<AllWarehousesResponse>(
    GET_ALL_WAREHOUSES_FOR_CONFLICT_CHECK
  );

  const checkConflicts = async (options: ConflictCheckOptions): Promise<ZipcodeConflictResult> => {
    setLoading(true);
    
    try {
      const { originalZipcodes, newZipcodes, currentWarehouseId, areas } = options;
      
      // 计算被删除的zipcode
      const removedZipcodes = originalZipcodes.filter(zipcode => !newZipcodes.includes(zipcode));
      
      // 计算新增的zipcode
      const addedZipcodes = newZipcodes.filter(zipcode => !originalZipcodes.includes(zipcode));
      
      console.log('Conflict check - removed zipcodes:', removedZipcodes);
      console.log('Conflict check - added zipcodes:', addedZipcodes);
      
      // 检查被删除的zipcode是否在areas中使用
      const conflictingAreas: Array<AreaType & { conflictingZipcodes: string[] }> = [];
      if (removedZipcodes.length > 0) {
        areas.forEach(area => {
          const conflictingZipcodes = area.zipcodeList.filter(zipcode => removedZipcodes.includes(zipcode));
          if (conflictingZipcodes.length > 0) {
            conflictingAreas.push({
              ...area,
              conflictingZipcodes
            });
          }
        });
      }
      
      // 检查新增的zipcode是否在其他warehouse中使用
      const conflictingWarehouses: Array<{ warehouseId: number; warehouseName: string; zipcodes: string[] }> = [];
      if (addedZipcodes.length > 0) {
        const { data } = await getAllWarehouses();
        
        console.log('All warehouses data:', data);
        
        if (data?.warehouses) {
          data.warehouses.forEach(warehouse => {
            // 跳过当前warehouse
            if (warehouse.id === currentWarehouseId) {
              return;
            }
            
            console.log(`Checking warehouse ${warehouse.id} (${warehouse.name}):`, warehouse.deliveryZoneZipcodeList);
            
            const conflictingZipcodes = addedZipcodes.filter(zipcode => 
              warehouse.deliveryZoneZipcodeList?.includes(zipcode)
            );
            
            if (conflictingZipcodes.length > 0) {
              console.log(`Found conflict with warehouse ${warehouse.id}:`, conflictingZipcodes);
              conflictingWarehouses.push({
                warehouseId: warehouse.id!,
                warehouseName: warehouse.name || `Warehouse ${warehouse.id}`,
                zipcodes: conflictingZipcodes
              });
            }
          });
        }
      }
      
      const result = {
        hasAreaConflict: conflictingAreas.length > 0,
        hasWarehouseConflict: conflictingWarehouses.length > 0,
        conflictingAreas,
        conflictingWarehouses
      };
      
      console.log('Conflict check result:', result);
      
      return result;
    } finally {
      setLoading(false);
    }
  };

  return {
    checkConflicts,
    loading
  };
}; 